// Builds the patient-facing medication string shown in the /me/history
// prescription and shipment tables, e.g.:
//   "Gabapentin 200mg (30 Tablets)"
//   "Propranolol 12mg (30 Raspberry Lemonade Troches)"
//   "Propranolol 12mg (12 All Flavors Troches)"   ← all-flavors sample pack
//
// The parenthetical combines quantity, an optional flavor, and the (pluralized)
// form factor. Flavor normally comes from the prescription's `subcategory`. The
// all-flavors sample pack is the exception: it ships a variety of flavors under
// one parent prescription, so that parent has NO single subcategory — we detect
// it from the prescription's order item (product slug) and label it "All
// Flavors". Commercial meds have no flavor, so it's omitted. When the
// prescription has no form factor we fall back to just "name dosage".

import pluralize from "pluralize";

import {
  FLAVOR_NAME_ALL_FLAVORS,
  ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS,
  SUBCATEGORY_TO_FLAVOR_DISPLAY,
} from "~/shared/sharedConstants";

// True when this prescription is the all-flavors sample pack, identified by its
// order item's product slug. `orderItems` are the visit's order items (the
// portal already fetches them); pass them from the call site so we can resolve
// rx.orderItemID → productSlug without a server change.
function isAllFlavorsSamplePack(rx, orderItems) {
  if (!orderItems || !rx.orderItemID) return false;
  const orderItem = orderItems.find((oi) => oi && oi.id === rx.orderItemID);
  return (
    !!orderItem &&
    orderItem.productSlug === ORDER_TYPE_PROP_SAMPLE_PACK_ALL_FLAVORS
  );
}

export function medicationLabel(rx, orderItems) {
  if (!rx) return "";
  const name = [rx.medicationName, rx.medicationDosage]
    .filter(Boolean)
    .join(" ");

  const form = rx.medicationFormFactor;
  if (!form) return name;

  let flavor = SUBCATEGORY_TO_FLAVOR_DISPLAY[rx.subcategory] || "";
  if (!flavor && isAllFlavorsSamplePack(rx, orderItems)) {
    flavor = FLAVOR_NAME_ALL_FLAVORS;
  }

  const qty = rx.quantity;

  const parts = [];
  if (qty) parts.push(String(qty));
  if (flavor) parts.push(flavor);
  parts.push(qty ? pluralize(form, qty) : form);

  return `${name} (${parts.join(" ")})`;
}
